import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { cifraService, grupoService } from '../lib/supabase'
import { buscarCifrasIA } from '../lib/ia'
import toast from 'react-hot-toast'
import PostarCifraModal from '../components/PostarCifraModal'
import CifraCard from '../components/CifraCard'
import TomModal from '../components/TomModal'

export default function FeedPage() {
  const { grupoId } = useParams()
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [grupo, setGrupo] = useState(null)
  const [cifras, setCifras] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pagina, setPagina] = useState(0)
  const [temMais, setTemMais] = useState(true)
  const [busca, setBusca] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [resultadosIA, setResultadosIA] = useState(null)
  const [showPostar, setShowPostar] = useState(false)
  const [cifraParaTom, setCifraParaTom] = useState(null)
  const [abaAtiva, setAbaAtiva] = useState('feed') // feed | membros

  useEffect(() => { init() }, [grupoId])

  async function init() {
    try {
      const membros = await grupoService.membrosDoGrupo(grupoId)
      const membro = membros.find(m => m.user_id === user?.id)
      if (!membro) { navigate('/grupos'); return }
      const { cifras: data, total } = await cifraService.listarCifras(grupoId, { pagina: 0 })
      setCifras(data)
      setTemMais(data.length < total)
      // Pega info do grupo
      const grupoInfo = await grupoService.meuGrupos(user.id)
      setGrupo(grupoInfo.find(g => g.id === grupoId))
    } catch (e) {
      toast.error('Erro ao carregar o feed')
    } finally {
      setLoading(false)
    }
  }

  async function carregarMais() {
    if (loadingMore || !temMais) return
    setLoadingMore(true)
    const novaPag = pagina + 1
    try {
      const { cifras: mais, total } = await cifraService.listarCifras(grupoId, { pagina: novaPag })
      setCifras(prev => [...prev, ...mais])
      setPagina(novaPag)
      setTemMais(cifras.length + mais.length < total)
    } finally {
      setLoadingMore(false)
    }
  }

  let buscaTimer
  async function handleBusca(valor) {
    setBusca(valor)
    clearTimeout(buscaTimer)
    if (!valor.trim()) { setResultadosIA(null); return }

    buscaTimer = setTimeout(async () => {
      setBuscando(true)
      try {
        // Primeiro busca localmente no grupo
        const local = await cifraService.buscarCifras(grupoId, valor)
        // Depois busca com IA para sugestões externas
        const ia = await buscarCifrasIA(valor)
        setResultadosIA({ local, ia: ia.sugestoes || [], dica: ia.dica_musical })
      } catch (e) {
        toast.error('Erro na busca')
      } finally {
        setBuscando(false)
      }
    }, 800)
  }

  function onNovaCifra(nova) {
    // Enriquece a cifra recém-criada com os dados do perfil do autor
    // (o Supabase retorna só a linha, sem joins)
    const cifraCompleta = {
      ...nova,
      profiles: profile
        ? { id: profile.id, nome: profile.nome, apelido: profile.apelido, avatar_url: profile.avatar_url }
        : null,
      curtidas: [{ count: 0 }],
      comentarios: [{ count: 0 }],
      favoritos: [{ count: 0 }]
    }
    setCifras(prev => [cifraCompleta, ...prev])
    setShowPostar(false)
  }

  const iniciais = (nome) => nome?.split(' ').map(p => p[0]).join('').substring(0,2).toUpperCase() || '?'

  return (
    <div className="app-container" style={{ background: 'var(--brand)' }}>

      {/* Header */}
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/grupos')} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>‹</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 18, fontWeight: 700 }}>
            {grupo?.nome || '...'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            {(() => { const c = (grupo?.codigo_convite||'').toUpperCase(); return c.length===8?`${c.slice(0,4)}-${c.slice(4)}`:c.length===6?`${c.slice(0,3)}-${c.slice(3)}`:c })()} · código
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', background: 'rgba(255,255,255,0.06)', padding: '6px 10px', borderRadius: 8 }}>
          {cifras.length} cifras
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '14px 20px' }}>
        <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 16 }}>🔍</span>
          <input value={busca} onChange={e => handleBusca(e.target.value)} placeholder="Nome, artista ou trecho da letra..." style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontFamily: 'Montserrat, sans-serif', fontSize: 14, flex: 1, width: '100%' }} />
          {buscando && <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />}
          {busca && <span onClick={() => { setBusca(''); setResultadosIA(null) }} style={{ color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>×</span>}
        </div>
      </div>

      {/* Resultados IA */}
      {resultadosIA && (
        <div style={{ padding: '0 20px 12px' }}>
          {resultadosIA.local.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>No seu grupo</div>
              {resultadosIA.local.map(c => (
                <div key={c.id} onClick={() => setCifraParaTom(c)} style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--card-border)', borderRadius: 10, marginBottom: 6, cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.titulo}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.artista} · Tom: <span style={{ color: 'var(--gold)' }}>{c.tom_ultima_vez || c.tom_original || '—'}</span></div>
                </div>
              ))}
            </>
          )}

          {resultadosIA.ia.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: 'var(--gold)', marginBottom: 8, marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <div className="ai-dots"><div className="ai-dot" /><div className="ai-dot" /><div className="ai-dot" /></div>
                Sugestões da IA
              </div>
              {resultadosIA.ia.map((s, i) => (
                <div key={i} style={{ padding: '12px 14px', background: 'rgba(0,207,255,0.05)', border: '1px solid rgba(0,207,255,0.15)', borderRadius: 10, marginBottom: 6, cursor: 'pointer' }}
                  onClick={() => setShowPostar({ preenchido: s })}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.titulo}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{s.artista} · Tom: <span style={{ color: 'var(--gold)' }}>{s.tom_original}</span> · {s.dificuldade}</div>
                  <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4 }}>+ Adicionar ao grupo</div>
                </div>
              ))}
              {resultadosIA.dica && (
                <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, marginTop: 6 }}>
                  💡 {resultadosIA.dica}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Feed */}
      <div className="page-content" style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="spinner" style={{ width: 32, height: 32 }} />
          </div>
        ) : cifras.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎵</div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Nenhuma cifra ainda</div>
            <div style={{ fontSize: 14, color: 'var(--muted)' }}>Seja o primeiro a compartilhar!</div>
          </div>
        ) : (
          <>
            {cifras.map(c => (
              <CifraCard key={c.id} cifra={c} userId={user?.id}
                onVerTom={() => setCifraParaTom(c)}
                onCurtir={() => cifraService.curtir(c.id, user.id)}
                onDeletada={(id) => setCifras(prev => prev.filter(x => x.id !== id))}
                modoVocal={profile?.modo_vocal || profile?.instrumento === 'Voz'} />
            ))}
            {temMais && (
              <button className="btn btn-block" onClick={carregarMais} disabled={loadingMore} style={{ marginTop: 8 }}>
                {loadingMore ? <span className="spinner" /> : 'Carregar mais'}
              </button>
            )}
          </>
        )}
      </div>

      {/* Bottom Nav */}
      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, background: 'rgba(2,18,53,0.97)', backdropFilter: 'blur(12px)', borderTop: '1px solid var(--card-border)', display: 'flex', padding: '10px 0 24px', zIndex: 50 }}>
        {[['🏠','Feed'], ['👥','Membros'], null, ['📅','Setlists'], ['🎼','Classic']].map((item, i) => {
          if (!item) return (
            <div key="add" style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <button onClick={() => setShowPostar(true)} style={{ width: 52, height: 52, borderRadius: '50%', marginTop: -26, background: 'linear-gradient(135deg, var(--brand2), var(--gold))', border: 'none', fontSize: 26, color: '#fff', cursor: 'pointer', boxShadow: '0 4px 20px rgba(255,140,0,0.4)' }}>+</button>
            </div>
          )
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', color: abaAtiva === item[1].toLowerCase() ? 'var(--brand2)' : 'var(--muted)' }}
              onClick={() => {
                if (item[1] === 'Setlists') navigate(`/grupo/${grupoId}/setlists`)
                else if (item[1] === 'Membros') navigate(`/grupo/${grupoId}/membros`)
                else if (item[1] === 'Classic') navigate(`/grupo/${grupoId}/classic`)
                else setAbaAtiva(item[1].toLowerCase())
              }}>
              <span style={{ fontSize: 20 }}>{item[0]}</span>
              <span style={{ fontSize: 10 }}>{item[1]}</span>
            </div>
          )
        })}
      </div>

      {/* Modais */}
      {showPostar && (
        <PostarCifraModal grupoId={grupoId} userId={user.id} profile={profile}
          preenchido={showPostar?.preenchido}
          onClose={() => setShowPostar(false)} onSuccess={onNovaCifra} />
      )}
      {cifraParaTom && (
        <TomModal cifra={cifraParaTom} userId={user.id} grupoId={grupoId}
          onClose={() => setCifraParaTom(null)}
          onRegistrar={tom => { cifraService.registrarTom(cifraParaTom.id, grupoId, user.id, tom, ''); setCifraParaTom(null); toast.success(`Tom ${tom} registrado!`) }} />
      )}

    </div>
  )
}
